import { useState } from 'react';
import { Clock, CheckCircle, XCircle, Info } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { Button } from '@/components/ui/button.js';
import { Badge } from '@/components/ui/badge.js';
import { useServices } from '@/contexts/ServicesContext.js';
import { useNavigate } from 'react-router-dom';
import type { PendingApproval } from '@/stores/approvalStore.js';
import { ApprovalDetailModal } from './ApprovalDetailModal.js';

interface ApprovalItemProps {
    approval: PendingApproval;
}

export function ApprovalItem({ approval }: ApprovalItemProps) {
    const { streamingService, sessionService } = useServices();
    const navigate = useNavigate();
    const [isProcessing, setIsProcessing] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);

    const handleApprove = async (e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation(); // Prevent navigation
        }
        try {
            setIsProcessing(true);
            streamingService.approveRequest(approval.approvalId);
        } catch (error) {
            console.error('Failed to approve request:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleReject = async (e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation(); // Prevent navigation
        }
        try {
            setIsProcessing(true);
            streamingService.rejectRequest(approval.approvalId);
        } catch (error) {
            console.error('Failed to reject request:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClick = async () => {
        try {
            // Load session and navigate
            await sessionService.switchSession(approval.sessionId);
            navigate(`/chat/${approval.sessionId}`);
        } catch (error) {
            console.error('[ApprovalItem] Failed to switch session:', error);
        }
    };

    const handleShowDetails = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowDetailModal(true);
    };

    // Generate user-friendly summary
    const getSummary = () => {
        if (approval.message) {
            return approval.message;
        }

        // Fallback: humanize tool name
        const humanized = approval.toolName
            .replace(/([A-Z])/g, ' $1')
            .replace(/_/g, ' ')
            .toLowerCase()
            .trim();
        return `Approval: ${humanized}`;
    };

    const summary = getSummary();
    const timeAgo = getTimeAgo(approval.timestamp);

    return (
        <>
            <div
                onClick={handleClick}
                className={cn(
                    'group relative flex flex-col gap-2 rounded-lg p-3 transition-all cursor-pointer',
                    'hover:bg-muted',
                    approval.status === 'pending' &&
                        'bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-400',
                    approval.status === 'approved' && 'bg-green-50 dark:bg-green-950/20 opacity-60',
                    approval.status === 'rejected' && 'bg-red-50 dark:bg-red-950/20 opacity-60'
                )}
            >
                {/* Header */}
                <div className="flex items-start gap-2">
                    <div className="flex-shrink-0 mt-0.5">
                        {approval.status === 'pending' && (
                            <Clock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                        )}
                        {approval.status === 'approved' && (
                            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                        )}
                        {approval.status === 'rejected' && (
                            <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
                        )}
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{summary}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{timeAgo}</p>
                    </div>
                </div>

                {/* Tool name and session info */}
                <div className="flex items-center justify-between gap-2 pl-6">
                    <div className="text-xs text-muted-foreground font-mono truncate">
                        {approval.toolName}
                    </div>
                    <Badge variant="outline" className="text-[10px] px-1 py-0">
                        {approval.sessionId.slice(0, 6)}
                    </Badge>
                </div>

                {/* Action buttons (only show for pending) */}
                {approval.status === 'pending' && (
                    <div className="flex gap-1.5 pl-6">
                        <Button
                            size="sm"
                            onClick={handleShowDetails}
                            disabled={isProcessing}
                            variant="outline"
                            className="h-7 w-7 p-0 flex-shrink-0"
                            title="View Details"
                        >
                            <Info className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleApprove}
                            disabled={isProcessing}
                            className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700 text-white min-w-0 px-2"
                        >
                            <CheckCircle className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="truncate">Approve</span>
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleReject}
                            disabled={isProcessing}
                            variant="destructive"
                            className="flex-1 h-7 text-xs min-w-0 px-2"
                        >
                            <XCircle className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="truncate">Reject</span>
                        </Button>
                    </div>
                )}
            </div>

            {/* Detail Modal */}
            <ApprovalDetailModal
                approval={approval}
                open={showDetailModal}
                onOpenChange={setShowDetailModal}
                onApprove={handleApprove}
                onReject={handleReject}
                isProcessing={isProcessing}
            />
        </>
    );
}

function getTimeAgo(timestamp: number): string {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);

    if (seconds < 60) {
        return 'just now';
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
        return `${minutes}m ago`;
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
        return `${hours}h ago`;
    }

    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}
